import { useState } from 'react';
import { ussd } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { TenantName } from '../../../components/TenantName';
import { formatDate, formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useUssdCodes, useSuspendUssdCode, useResumeUssdCode, useReleaseUssdCode } from '../../../data/useUssd';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
const STATUS_COLOUR: Record<ussd.UssdCodeStatus, string> = { live: 'green', suspended: 'orange', released: 'gray' };

/**
 * Every code the platform has given out. Suspending stops a code answering
 * (its rental is still charged); resuming lets it answer again; releasing
 * takes it back for good, so it stops answering and is no longer charged.
 */
export function CodesCard() {
  const t = useT();
  const codes = useUssdCodes();
  const suspend = useSuspendUssdCode();
  const resume = useResumeUssdCode();
  const release = useReleaseUssdCode();
  const [suspending, setSuspending] = useState<ussd.UssdCode | null>(null);
  const [reason, setReason] = useState('');
  const rows = (codes.data?.data ?? []) as Indexed<ussd.UssdCode>[];
  const fail = (e: unknown) => notifyError(e instanceof Error ? e.message : t('ussd.toast.failed'));

  const doSuspend = async () => {
    if (!suspending) return;
    try {
      await suspend.mutateAsync({ id: suspending.id, reason: reason.trim() || undefined });
      notifySuccess(t('ussd.codes.suspended', { code: suspending.code }));
      setSuspending(null); setReason('');
    } catch (e) { fail(e); }
  };
  const doResume = async (c: ussd.UssdCode) => {
    try { await resume.mutateAsync(c.id); notifySuccess(t('ussd.codes.resumed', { code: c.code })); } catch (e) { fail(e); }
  };
  const confirmRelease = (c: ussd.UssdCode) => openConfirmModal({
    title: t('ussd.codes.releaseTitle', { code: c.code }),
    children: <Text role="bodySmall">{t('ussd.codes.releaseBody', { code: c.code })}</Text>,
    labels: { confirm: t('ussd.codes.release'), cancel: t('action.cancel') },
    confirmProps: { color: 'red' },
    onConfirm: async () => {
      try { await release.mutateAsync(c.id); notifySuccess(t('ussd.codes.released', { code: c.code })); } catch (e) { fail(e); }
    }
  });

  return (
    <Card withBorder data-testid="ussd-codes">
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('ussd.codes.title')}</Text>
          <Text role="bodySmall" c={MUTED}>{t('ussd.codes.hint')}</Text>
        </Stack>
        {codes.isError && (
          <Alert color="error" role="alert">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('ussd.loadFailed')}</Text>
              <Button variant="outline" size="xs" onClick={() => void codes.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        {!codes.isPending && !codes.isError && rows.length === 0 && (
          <EmptyState title={t('ussd.codes.empty')} description={t('ussd.codes.emptyHint')} />
        )}
        {(codes.isPending || rows.length > 0) && (
          <DataTable
            records={rows}
            idAccessor="id"
            loading={codes.isPending}
            columns={[
              {
                accessor: 'code', title: t('ussd.codes.code'),
                render: (r) => (
                  <Stack gap="xs">
                    <code className="rr-ussd__code">{r.code}</code>
                    <Text role="caption" c={MUTED}>{t(`ussd.kind.${r.kind}`)}{r.provider_code ? ` · ${r.provider_code}` : ''}</Text>
                  </Stack>
                )
              },
              { accessor: 'organisation_id', title: t('ussd.col.organisation'), render: (r) => <TenantName id={r.organisation_id} /> },
              {
                accessor: 'status', title: t('ussd.col.status'),
                render: (r) => (
                  <Stack gap="xs">
                    <Badge variant="light" color={STATUS_COLOUR[r.status]}>{t(`ussd.codes.status.${r.status}`)}</Badge>
                    {r.suspended_reason && r.status === 'suspended' && <Text role="caption" c={MUTED}>{r.suspended_reason}</Text>}
                    {r.overdue_since && <Text role="caption" c="var(--rr-color-error)">{t('ussd.codes.overdue', { when: formatDate(r.overdue_since) })}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'next_rental_at', title: t('ussd.codes.rental'),
                render: (r) => (r.status === 'released'
                  ? <Text role="caption" c={MUTED}>{r.released_at ? t('ussd.codes.releasedOn', { when: formatDate(r.released_at) }) : '–'}</Text>
                  : (
                    <Stack gap="xs">
                      <Text role="bodySmall" title={r.next_rental_at ? formatDateTime(r.next_rental_at) : undefined}>{r.next_rental_at ? formatDate(r.next_rental_at) : '–'}</Text>
                      {r.approved_at && <Text role="caption" c={MUTED}>{t('ussd.codes.approvedOn', { when: formatDate(r.approved_at) })}</Text>}
                    </Stack>
                  ))
              },
              {
                accessor: 'actions', title: t('common.actions'),
                render: (r) => (
                  <Group gap="xs" wrap="nowrap">
                    {r.status === 'live' && <Button size="xs" variant="default" onClick={() => setSuspending(r)}>{t('ussd.codes.suspend')}</Button>}
                    {r.status === 'suspended' && (
                      <Button size="xs" variant="default" loading={resume.isPending && resume.variables === r.id} onClick={() => void doResume(r)}>{t('ussd.codes.resume')}</Button>
                    )}
                    {r.status !== 'released' && <Button size="xs" variant="subtle" color="red" onClick={() => confirmRelease(r)}>{t('ussd.codes.release')}</Button>}
                  </Group>
                )
              }
            ]}
          />
        )}
      </Stack>
      <Modal opened={suspending !== null} onClose={() => { setSuspending(null); setReason(''); }} title={t('ussd.codes.suspendTitle', { code: suspending?.code ?? '' })}>
        <Stack gap="md">
          <Text role="bodySmall">{t('ussd.codes.suspendBody')}</Text>
          <Textarea label={t('ussd.codes.suspendReason')} description={t('ussd.codes.suspendReasonHint')} value={reason} minRows={2} maxLength={200} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => { setSuspending(null); setReason(''); }}>{t('action.cancel')}</Button>
            <Button color="red" loading={suspend.isPending} onClick={() => void doSuspend()}>{t('ussd.codes.suspend')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
